import React from 'react'

interface Props {
  children: React.ReactNode
  /** What broke, in the user's terms — e.g. "The sandbox". */
  label?: string
  /**
   * Changing this clears a latched error. Pass whatever identifies the thing
   * being rendered (the current view, the open modal) so navigating away is
   * enough to recover.
   */
  resetKey?: unknown
  /** Extra recovery step, e.g. closing the modal that blew up. */
  onReset?: () => void
  /** 'overlay' centres the fallback over the app, for modal-shaped children. */
  variant?: 'inline' | 'overlay'
}

interface State {
  error: Error | null
}

/**
 * Stops one broken component taking the whole app down with it. React unmounts
 * the entire tree on an uncaught render error, which turns a single bad value
 * into a blank white page.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Caught by ErrorBoundary:', error, info.componentStack)
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  private handleRetry = () => {
    this.setState({ error: null })
    this.props.onReset?.()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const { label = 'This part of the app', variant = 'inline' } = this.props

    const card = (
      <div className="bg-white border border-line rounded p-5 max-w-md">
        <h2 className="font-display text-lg text-ink mb-2">{label} stopped working</h2>
        <p className="text-sm text-ink/80 mb-3">
          Something in here hit an error and couldn't finish drawing. The rest of the app is still
          running, and your data is saved in this browser — nothing has been changed or lost.
        </p>
        <details className="mb-4">
          <summary className="text-xs text-ink/70 cursor-pointer hover:text-ink">
            Show the error
          </summary>
          <pre className="mt-2 text-[11px] font-mono text-rag-bad whitespace-pre-wrap break-words">
            {error.message || String(error)}
          </pre>
        </details>
        <button
          onClick={this.handleRetry}
          className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90"
        >
          Try again
        </button>
      </div>
    )

    if (variant === 'overlay') {
      return <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-50">{card}</div>
    }
    return <div className="p-6">{card}</div>
  }
}
